import { useState } from 'react';
import type { Seedling as SeedlingType, LocalTrayState } from '../lib/types';
import { SeedlingSprite } from './Seedling';
import { Plus } from 'lucide-react';

interface Props {
  state: LocalTrayState;
  onPlant: (cell: number) => void;
  onTend: (seedling: SeedlingType) => void;
}

export function TrayGrid({ state, onPlant, onTend }: Props) {
  const cells = Array.from({ length: state.tray.cell_count }, (_, i) => i);
  const [, setPlantingCell] = useState<number | null>(null);

  const getSeedling = (cell: number) =>
    state.seedlings.find(s => s.cell_index === cell && !s.retracted_at);

  const getTendCount = (s: SeedlingType) =>
    state.tendings.filter(t => t.seedling_id === s.id).length;

  function startPlant(cell: number) {
    const existing = getSeedling(cell);
    if (existing) onTend(existing);
    else {
      setPlantingCell(cell);
      onPlant(cell);
      // The actual plant logic lives in parent (App) via onPlant callback which opens a form
    }
  }

  return (
    <div className="tray p-6 md:p-8">
      <div className="grid grid-cols-4 gap-3 md:gap-4 place-items-center">
        {cells.map(cell => {
          const s = getSeedling(cell);
          const count = s ? getTendCount(s) : 0;
          return (
            <div key={cell} className={`pot w-[92px] h-[92px] md:w-[102px] md:h-[102px] rounded-2xl flex items-center justify-center ${s ? '' : 'empty'}`}>
              {s ? (
                <SeedlingSprite
                  seedling={s}
                  tendCount={count}
                  onClick={() => onTend(s)}
                  size={82}
                />
              ) : (
                <button
                  onClick={() => startPlant(cell)}
                  className="flex flex-col items-center text-[var(--text-subtle)] hover:text-[var(--accent)] transition group"
                  aria-label={`Plant in cell ${cell + 1}`}
                >
                  <div className="w-9 h-9 rounded-full border border-dashed border-[var(--border)] flex items-center justify-center group-hover:border-[var(--accent)]">
                    <Plus size={17} />
                  </div>
                  <div className="text-[10px] mt-1 tracking-wide">plant</div>
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-4 text-[10px] text-center text-[var(--text-subtle)]">
        {state.seedlings.filter(s => !s.retracted_at).length} seedlings • {state.tendings.length} tendings • click a sprout to tend it
      </div>
    </div>
  );
}
